import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm } from "@inertiajs/react"
import { IconArrowLeft, IconInfoCircle, IconSparkles } from "@tabler/icons-react"
import { useRef } from "react"

export default function InvoiceImport() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data, setData, post, processing } = useForm<{
    csv_file: File | null
  }>({
    csv_file: null,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/invoices/process_import", {
      forceFormData: true,
    })
  }

  return (
    <>
      <Head title="Import Invoices - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/invoices">Invoices</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Import</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/invoices">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Import Invoices from CSV</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Upload CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="csv_file">Select CSV File</Label>
                  <input
                    ref={fileInputRef}
                    id="csv_file"
                    type="file"
                    accept=".csv"
                    required
                    onChange={(e) => setData("csv_file", e.target.files?.[0] || null)}
                    className="border-input bg-background block w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                  />
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                  <div className="flex gap-3">
                    <IconInfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800 dark:text-blue-300">CSV Format Requirements</p>
                      <div className="mt-2 space-y-2 text-blue-700 dark:text-blue-400">
                        <p>Your CSV file should have the following columns with headers:</p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>
                            <strong>Client (drop down)</strong> - Customer name (required)
                          </li>
                          <li>
                            <strong>Value (currency)</strong> - Invoice total amount (required)
                          </li>
                          <li>
                            <strong>Currency (drop down)</strong> - Currency code (USD, UYU, etc.)
                          </li>
                          <li>
                            <strong>Invoiced On (date)</strong> - Invoice date (required)
                          </li>
                          <li>
                            <strong>Status</strong> - Invoice status (draft, sent, invoice paid, etc.)
                          </li>
                          <li>
                            <strong>Payment method (drop down)</strong> - Optional
                          </li>
                          <li>
                            <strong>Task Name</strong> - Description (optional)
                          </li>
                        </ul>

                        <p className="pt-2 font-medium">Example CSV format:</p>
                        <div className="overflow-x-auto rounded border border-blue-200 bg-white p-2 font-mono text-xs dark:border-blue-800 dark:bg-gray-900">
                          &quot;Client (drop down)&quot;,&quot;Task Name&quot;,&quot;Value
                          (currency)&quot;,&quot;Currency (drop down)&quot;,&quot;Status&quot;,&quot;Invoiced On
                          (date)&quot;,&quot;Payment method (drop down)&quot;
                          <br />
                          &quot;Hey Spanish&quot;,&quot;Website Design&quot;,161,&quot;USD&quot;,&quot;invoice
                          paid&quot;,&quot;Thursday, August 31st 2023&quot;,&quot;PayPal&quot;
                        </div>

                        <p className="pt-2">
                          <strong>Supported Currencies:</strong> USD, UYU, PESO, PESOS
                        </p>
                        <p>
                          <strong>Supported Status Values:</strong> invoice paid, paid, invoice sent, sent, draft,
                          overdue, cancelled
                        </p>
                      </div>

                      <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/30">
                        <div className="flex items-start gap-2">
                          <IconSparkles className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="font-medium text-green-800 dark:text-green-300">
                              Automatic Customer Creation
                            </p>
                            <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-green-700 dark:text-green-400">
                              <li>Customers that don&apos;t exist will be created automatically</li>
                              <li>New customers get placeholder contact info you can update later</li>
                              <li>Due date is automatically set to 30 days from invoice date</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="ghost" asChild>
                  <a href="/invoices">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing || !data.csv_file}>
                  {processing ? "Importing..." : "Import CSV"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </div>
    </>
  )
}

InvoiceImport.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
